import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from './hooks'
import { fetchStockPrices, fetchCryptoPrices, fetchFxRates, upsertCryptoPrice } from './pricesSlice'
import { BinancePriceStream } from '@/lib/binance'
import type { Asset } from '@/features/assets/api/types'

const STOCK_POLL_INTERVAL = 60 * 1000           // 60s trong giờ giao dịch VN
const FX_POLL_INTERVAL = 5 * 60 * 1000          // 5 phút

// Ngày lễ cố định — thị trường CK VN nghỉ
const FIXED_HOLIDAYS = new Set(['01-01', '04-30', '05-01', '09-02'])

/** T2-T6, 9:00-15:00 Asia/Ho_Chi_Minh */
function isStockMarketOpen(): boolean {
  const now = new Date()
  const vnTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Ho_Chi_Minh' }))
  const day = vnTime.getDay()
  if (day === 0 || day === 6) return false
  const mm = String(vnTime.getMonth() + 1).padStart(2, '0')
  const dd = String(vnTime.getDate()).padStart(2, '0')
  if (FIXED_HOLIDAYS.has(`${mm}-${dd}`)) return false
  const mins = vnTime.getHours() * 60 + vnTime.getMinutes()
  return mins >= 9 * 60 && mins <= 15 * 60
}

/**
 * Polling prices + fx rates. GỌI 1 LẦN DUY NHẤT ở AppLayout.
 * Các component khác dùng `usePrices()` để đọc data, không trigger polling.
 */
export function usePricesPolling() {
  const dispatch = useAppDispatch()
  const assets = useAppSelector((s) => s.assets.items)
  const assetsStatus = useAppSelector((s) => s.assets.status)

  const stockKey = assets.filter((a) => a.type === 'stock' && a.symbol).map((a) => a.symbol!).join(',')
  const cryptoKey = assets.filter((a) => a.type === 'crypto' && a.symbol).map((a) => a.symbol!).join(',')

  // Fetch stock lần đầu khi có stock
  useEffect(() => {
    if (assetsStatus !== 'succeeded' || !stockKey) return
    dispatch(fetchStockPrices())
  }, [dispatch, assetsStatus, stockKey])

  // Polling stock theo giờ giao dịch VN
  useEffect(() => {
    if (assetsStatus !== 'succeeded' || !stockKey) return
    const interval = setInterval(() => {
      if (isStockMarketOpen()) dispatch(fetchStockPrices())
    }, STOCK_POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [dispatch, assetsStatus, stockKey])

  // Fetch crypto lần đầu khi có crypto (REST direct, fallback Worker)
  useEffect(() => {
    if (assetsStatus !== 'succeeded' || !cryptoKey) return
    dispatch(fetchCryptoPrices())
  }, [dispatch, assetsStatus, cryptoKey])

  // WebSocket realtime stream từ Binance, thay cho polling 60s
  const streamRef = useRef<BinancePriceStream | null>(null)
  useEffect(() => {
    if (assetsStatus !== 'succeeded' || !cryptoKey) {
      streamRef.current?.stop()
      streamRef.current = null
      return
    }
    const symbols = cryptoKey.split(',').filter(Boolean)
    if (!streamRef.current) {
      streamRef.current = new BinancePriceStream()
      streamRef.current.start(symbols, (tick) => {
        dispatch(upsertCryptoPrice(tick))
      })
    } else {
      streamRef.current.setSymbols(symbols)
    }
    return () => {
      // Chỉ stop khi component unmount (AppLayout). Khi cryptoKey đổi,
      // effect chạy lại sẽ gọi setSymbols thay vì teardown.
    }
  }, [dispatch, assetsStatus, cryptoKey])

  // Cleanup stream khi unmount
  useEffect(() => {
    return () => {
      streamRef.current?.stop()
      streamRef.current = null
    }
  }, [])

  // Fetch fx rates lần đầu + polling
  useEffect(() => {
    dispatch(fetchFxRates())
    const interval = setInterval(() => dispatch(fetchFxRates()), FX_POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [dispatch])
}

/**
 * Đọc prices từ Redux. KHÔNG trigger polling — chỉ select state.
 * An toàn để gọi nhiều lần từ nhiều component.
 */
export function usePrices() {
  const {
    stockPrices, stockUpdatedAt, stockStatus,
    cryptoPrices, cryptoUpdatedAt, cryptoStatus,
    fxRates, fxUpdatedAt,
  } = useAppSelector((s) => s.prices)

  const getPrice = (type: string, symbol: string | null): number => {
    if (type === 'cash') return 1
    if (type === 'stock' && symbol) return stockPrices[symbol] ?? 0
    if (type === 'crypto' && symbol) return cryptoPrices[symbol] ?? 0
    return 0
  }

  const getPriceForAsset = (asset: Asset): number => getPrice(asset.type, asset.symbol)

  // Merged updatedAt map cho backward compat
  const updatedAt = { ...stockUpdatedAt, ...cryptoUpdatedAt }

  return {
    stockPrices,
    cryptoPrices,
    fxRates,
    fxUpdatedAt,
    prices: { ...stockPrices, ...cryptoPrices },  // backward compat
    updatedAt,
    isLoading: stockStatus === 'loading' || cryptoStatus === 'loading',
    getPrice,
    getPriceForAsset,
  }
}
